import { observer } from 'mobx-react-lite';
import { FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, useState, useRef, useEffect } from 'react';
import styles from '@components/Textarea/Textarea.styles';
import InputBottomDescription from '@components/InputBottomDescription/InputBottomDescription';
type InternalInputType = {
  field: FieldState<string | null> | FieldState<string>;
  placeholder: string;
  disabled: boolean;
  focused: boolean;
  focusIn: () => void;
  focusOut: () => void;
  maxLength: number;
  cy?: string;
  required?: boolean;
  id?: string;
  addSx?: Record<string, unknown>;
};
const InternalInput = observer(({
  field,
  placeholder,
  disabled,
  focused,
  focusIn,
  focusOut,
  maxLength,
  cy,
  required = false,
  id,
  addSx = undefined
}: InternalInputType): JSX.Element => {
  const commonTrans = useTranslation('common');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    value,
    hasError,
    onChange
  } = field;
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '48px';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, 200)}px`;
    }
  };
  useEffect(() => {
    adjustHeight();
  }, [value]);
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    adjustHeight();
  };
  return <textarea id={id} ref={textareaRef} placeholder={focused ? undefined : commonTrans.t(placeholder)} disabled={disabled} sx={addSx ? {
    ...styles.textarea(hasError, disabled),
    ...addSx
  } : styles.textarea(hasError, disabled)} value={value || ''} maxLength={maxLength || undefined} name={placeholder} onChange={handleChange} onFocus={focusIn} onBlur={focusOut} data-testid={cy || 'textarea-field'} aria-required={required} required={required} />;
});
type PropsType = {
  field: FieldState<string | null> | FieldState<string>;
  placeholder: string;
  disabled?: boolean;
  margin?: string;
  width?: string;
  maxLength?: number;
  bottomDescription?: string;
  cy?: string;
  required?: boolean;
  addSx?: Record<string, unknown>;
};
const Textarea = ({
  field,
  placeholder,
  disabled = false,
  margin = '0',
  width = '100',
  maxLength = 0,
  bottomDescription = '',
  cy = '',
  required = false,
  addSx = undefined
}: PropsType) => {
  const [focused, setFocused] = useState(false);
  const commonTrans = useTranslation('common');
  const {
    hasError
  } = field;
  const handleFocus = () => setFocused(true);
  const handleBlur = () => {
    void field.validate();
    setFocused(false);
  };
  const hasValue = field.value;
  const displayLabel = focused || !!hasValue;
  const textareaId = placeholder;
  return <div sx={styles.textareaContainer(hasError, margin, width)} data-testid={cy ? `${cy}-container` : 'textarea-container'}>
      <label htmlFor={textareaId} sx={styles.label(!!field.error, displayLabel, disabled)} data-testid={cy ? `${cy}-label` : 'textarea-label'}>
        {displayLabel ? commonTrans.t(placeholder) : ''}
      </label>

      <InternalInput field={field} placeholder={placeholder} disabled={disabled} focused={focused} focusIn={handleFocus} focusOut={handleBlur} maxLength={maxLength} cy={cy} required={required} id={textareaId} addSx={addSx} />

      {bottomDescription && <InputBottomDescription description={bottomDescription} length={field.value?.length || 0} maxLength={maxLength} />}

      {hasError && <p sx={styles.error(hasError)} data-testid={cy ? `${cy}-error` : 'textarea-error'}>
          {hasError && commonTrans.t(`${field.error}`)}
        </p>}
    </div>;
};
export default observer(Textarea);